import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetTitle, useFeatureFlags, useForm, useSnackbar } from '../../hooks';
import { useLogin, ILoginRequest } from '../../services';
import { BookingShell, InlineError } from '../book/components/BookingShell.component';

const imagesBucket = process.env.REACT_APP_AWS_BUCKET_IMAGES || '';
const BACKDROP = `https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Log-In.png`;

const inputCls = (bad: boolean) => `h-11 w-full rounded-xl border bg-surface px-3 text-sm outline-none transition focus:ring-2 focus:ring-emerald-600 ${bad ? 'border-red-400' : 'border-surface-border'}`;

// #region Login
/** Sign in, in the same card and photo as the sign-up page. Signing in itself is unchanged. */
const Login: React.FunctionComponent<{}> = (): JSX.Element => {
    const setTitle = useSetTitle();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [reveal, setReveal] = React.useState(false);
    const [tried, setTried] = React.useState(false);
    const [showCreateAccount, setShowCreateAccount] = React.useState<boolean>(false);
    const [FeatureFlagFunctions] = useFeatureFlags();
    const [user, , loginFunctions] = useLogin();
    const [snackbar, setSnackbar] = useSnackbar();

    const location = useLocation();
    const navigate = useNavigate();

    const form = useForm([
        { key: 'email', validation: (value: string) => (value === '' ? 'Enter your email' : null) },
        { key: 'password', validation: (value: string) => (value === '' ? 'Enter your password' : null) }
    ]);

    React.useEffect(() => {
        setTitle('Login');
        FeatureFlagFunctions.get('show-create-account').then(setShowCreateAccount);
    }, []);

    React.useEffect(() => {
        if (user !== null) {
            const queryParams = location.search.replace('?', '').split('&');
            const ref = queryParams.find((value: string) => value.includes('ref='));
            navigate(ref ? decodeURIComponent(ref.replace('ref=', '')) : '/');
        }
    }, [user]);

    const email = form.valueAtKey('email') ?? '';
    const password = form.valueAtKey('password') ?? '';

    const callLogin = async () => {
        setTried(true);
        if (!email || !password) return;
        // A fresh try clears the last failure; the login service reports a new one the same way.
        setSnackbar({ ...snackbar, show: false });
        setLoading(true);
        const loginRequest: ILoginRequest = { password, email };
        await loginFunctions.login(loginRequest);
        setLoading(false);
    };

    return (
        <BookingShell backdrop={BACKDROP} compact footer={null}
            label={showCreateAccount ? <>New to Wallmates? <a href="/new-account" className="font-semibold underline underline-offset-2">Create an account</a></> : ''}>
            <form className="flex flex-col gap-y-5 p-5 sm:p-7" noValidate onSubmit={(ev) => { ev.preventDefault(); callLogin(); }}>
                <div>
                    <h2 className="text-xl font-semibold tracking-tight">Sign in</h2>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Welcome back to Wallmates.</p>
                </div>

                <label className="flex flex-col gap-y-1 text-sm font-medium">
                    <span>Email address</span>
                    <input type="email" name="email" autoComplete="username" autoFocus value={email} aria-invalid={tried && !email}
                        onChange={(ev) => form.setValueAtKey('email', ev.target.value)} className={inputCls(tried && !email)} />
                    {tried && !email ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">Enter your email</span> : null}
                </label>

                <div className="flex flex-col gap-y-1 text-sm font-medium">
                    <div className="flex items-center justify-between">
                        <label htmlFor="login-password">Password</label>
                        <a href="/forgot-password" className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400">Forgot password?</a>
                    </div>
                    <div className="relative">
                        <input id="login-password" type={reveal ? 'text' : 'password'} name="password" autoComplete="current-password" value={password}
                            aria-invalid={tried && !password} onChange={(ev) => form.setValueAtKey('password', ev.target.value)}
                            className={`${inputCls(tried && !password)} pr-10`} />
                        <button type="button" onClick={() => setReveal(!reveal)} aria-label={reveal ? 'Hide password' : 'Show password'}
                            className="absolute inset-y-0 right-2 my-auto h-7 w-7 rounded-md p-1 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">
                            {reveal ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                    </div>
                    {tried && !password ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">Enter your password</span> : null}
                </div>

                <InlineError message={snackbar.show && !loading ? snackbar.text : null} />

                <button type="submit" disabled={loading} className="inline-flex h-11 items-center justify-center rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-60">
                    {loading ? 'Signing in…' : 'Sign in'}
                </button>
            </form>
        </BookingShell>
    );
};

export default Login;
